import { ApiTokenList, CreatedToken, OkResponse, type CreatedToken as Created } from '@mtg/shared';
import { useState, type FormEvent } from 'react';
import { api, errorText } from '../api.ts';
import { formatDateTime } from '../format.ts';
import { useApi } from '../useApi.ts';

export function SettingsPage() {
  const { data, error, reload } = useApi(() => api('/api/tokens', ApiTokenList), []);
  const [name, setName] = useState('');
  const [created, setCreated] = useState<Created | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function create(e: FormEvent) {
    e.preventDefault();
    setActionError(null);
    try {
      setCreated(await api('/api/tokens', CreatedToken, { body: { name } }));
      setName('');
      reload();
    } catch (err) {
      setActionError(errorText(err));
    }
  }

  async function revoke(id: string, tokenName: string) {
    if (!confirm(`Revoke "${tokenName}"? Scripts using it stop working at once.`)) return;
    setActionError(null);
    try {
      await api(`/api/tokens/${id}`, OkResponse, { method: 'DELETE' });
      if (created?.token.id === id) setCreated(null);
      reload();
    } catch (err) {
      setActionError(errorText(err));
    }
  }

  return (
    <div className="stack">
      <h1>Settings</h1>
      <h2>Write tokens</h2>
      <p className="muted">
        A write token lets Claude’s scripts read and change the collection and decks for you. It can’t sign in or manage tokens. Each change it
        makes shows in the log under the token’s name.
      </p>
      {created && (
        <div className="panel stack" role="status">
          <p>
            <strong>Copy this now; it won’t be shown again.</strong> Save it on its own in a file outside any project folder, for example{' '}
            <code>~/.mtg-app/token</code> (on Windows, <code>%USERPROFILE%\.mtg-app\token</code>).
          </p>
          <input readOnly value={created.secret} aria-label="New token" onFocus={(e) => e.target.select()} className="mono" />
          <div className="row">
            <button type="button" className="btn" onClick={() => void navigator.clipboard.writeText(created.secret)}>
              Copy
            </button>
            <button type="button" className="btn" onClick={() => setCreated(null)}>
              Done
            </button>
          </div>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      {data && data.tokens.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Starts with</th>
              <th>Created</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.tokens.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td className="mono">{t.prefix}…</td>
                <td className="nowrap">{formatDateTime(t.createdAt)}</td>
                <td className="nowrap">{t.lastUsedAt ? formatDateTime(t.lastUsedAt) : 'Never'}</td>
                <td>
                  <button type="button" className="btn small danger" onClick={() => void revoke(t.id, t.name)}>
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form className="row" onSubmit={create}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Claude (Windows PC)" maxLength={50} aria-label="Token name" />
        <button type="submit" className="btn primary" disabled={!name.trim()}>
          Create token
        </button>
      </form>
      {actionError && (
        <p className="error" role="alert">
          {actionError}
        </p>
      )}
    </div>
  );
}
